import i18nFactory from '@yandex-int/i18n';
import { compact, uniqBy } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { changeLocationHard, updateRegions } from '../../actions/common';
import {
    ADD_PHRASES_START_INPUT_ITEMS,
    addPhrasesStartInputItems,
    AddPhrasesStartInputItems,
    addPhrasesToMinusWordsList,
    AddSearchedAlsoPhrasesToStartPhrases,
    closePhrasesSelectorWizard,
    FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD,
    FetchPhrasesForPhrasesSelectorWizardAction,
    OPEN_PHRASES_SELECTOR_WIZARD,
    OpenPhrasesSelectorWizard,
    PHRASES_SELECTOR_ADD_PHRASES_TO_START_LIST,
    PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST,
    PHRASES_SELECTOR_SEND_MINUS_PHRASE_DATA,
    PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD,
    phrasesSelectorChangeInput,
    PhrasesSelectorSendMinusPhraseData,
    RemovePhrasesToMinusWordsList,
    selectionChange,
    SelectionWizardListName,
    showPhrasesSelectorWizard,
    SubmitSelectionWizard,
    updatePhrasesSelectorWizardState,
    updatePhrasesWizardError
} from '../../actions/phrasesSelectorWizard';
import { GetKeywordStatShowResponse, RestApiManager } from '../../api/RestApiManager';
import { phraseIdFieldName } from '../../declarations/filter/filter';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import { mapMinusPhraseValidationResultApiData } from '../../mappers/minus-phrase/minus-phrase';
import { mapPhraseToPhrasesSelectorListItem } from '../../mappers/phrasesSelectorWizard';
import { awaitCommonData } from '../../operators';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { serializeFilterToString } from '../../service/FilterHelpers/FilterHelpers';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { GRID_PREFIX, phrases } from '../../typings/common';
import { CampaignType, GroupType } from '../../typings/domain';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import { GridScreenUrlSearchQueryParams, NoticeType } from '../../typings/grid';
import { PhrasesSelectorInputColumns, PhrasesSelectorWizardState } from '../../typings/phrases';
import { State } from '../../typings/state';

import * as keyset from './phrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

interface KeywordsStatRequestParams {
    groupType: GroupType;
    phrasesList: string[];
    commonMinusPhrases: string[];
    regionsIds: number[];
    apiManager: RestApiManager;
}

async function keywordsStatRequest({
    groupType,
    phrasesList,
    commonMinusPhrases,
    regionsIds,
    apiManager
}: KeywordsStatRequestParams): Promise<GetKeywordStatShowResponse[]> {
    const result: GetKeywordStatShowResponse[] = [];

    for (const ph of phrasesList) {
        const keywordStat = await apiManager.getKeywordStatShow({
            adGroupType: groupType,
            phrase: ph,
            commonMinusPhrases,
            geo: regionsIds
        });

        result.push(keywordStat);
    }

    return result;
}

export const submitStartPhraseInputValueEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ADD_PHRASES_START_INPUT_ITEMS),
        mergeMap(
            (action: AddPhrasesStartInputItems): Observable<Action> => {
                const state = store$.value;

                const phrasesSelectorWizard: PhrasesSelectorWizardState = state.phrasesSelectorWizard[action.wizardId];
                const groupType = phrasesSelectorWizard.groupInfo.groupType;

                // старт-фразы
                const phrasesList = phrasesSelectorWizard.startPhrases.map(el => el.text).concat(action.value);

                // все минус-фразы
                const commonMinusPhrases = phrasesSelectorWizard.minusWordsList.map(el => el.text);

                const request = keywordsStatRequest({
                    groupType,
                    phrasesList,
                    commonMinusPhrases,
                    regionsIds: phrasesSelectorWizard.groupInfo.regionIds,
                    apiManager: deps.restApiManager
                });

                return from(request).pipe(
                    mergeMap((result: GetKeywordStatShowResponse[]): Action[] => {
                        const err = result.find(res => res.advqErrors.length > 0);
                        if (err) {
                            return [
                                updatePhrasesSelectorWizardState(action.wizardId, {
                                    isInProgress: false
                                }),
                                updatePhrasesWizardError(
                                    action.wizardId,
                                    i18n('invalid-query'),
                                    PhrasesSelectorInputColumns.SEARCH_VALUE
                                )
                            ];
                        }
                        const keywordsStat = phrasesList.reduce((acc, el, index) => {
                            acc[el] = {
                                searchedWith: result[index].searchedWith.map(mapPhraseToPhrasesSelectorListItem),
                                searchedAlso: result[index].searchedAlso.map(mapPhraseToPhrasesSelectorListItem)
                            };

                            return acc;
                        }, {});

                        const newPhrases = phrasesList.filter(
                            el => !phrasesSelectorWizard.startPhrases.find(item => item.text === el)
                        );

                        const newStartPhrasesList = newPhrases.map(el =>
                            mapPhraseToPhrasesSelectorListItem({
                                shows: 0,
                                phrase: el
                            })
                        );

                        const startPhrases = uniqBy(
                            [...phrasesSelectorWizard.startPhrases, ...newStartPhrasesList],
                            'text'
                        );

                        const actions = compact([
                            updatePhrasesSelectorWizardState(action.wizardId, {
                                keywordsStat,
                                startPhrases,
                                isInProgress: false
                            }),
                            selectionChange(
                                action.wizardId,
                                startPhrases.map(el => el.selected || newPhrases.includes(el.text)),
                                SelectionWizardListName.startPhrases
                            ),
                            phrasesSelectorChangeInput(action.wizardId, '', PhrasesSelectorInputColumns.SEARCH_VALUE)
                        ]);

                        return actions;
                    })
                );
            }
        )
    );
};

export const sendMinusPhraseEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_SEND_MINUS_PHRASE_DATA),
        mergeMap(
            (action: PhrasesSelectorSendMinusPhraseData): Observable<Action> => {
                const state = store$.value;

                const phrasesSelectorWizard = state.phrasesSelectorWizard[action.wizardId];
                const groupType = phrasesSelectorWizard.groupInfo.groupType;

                // старт-фразы
                const phrasesList = phrasesSelectorWizard.startPhrases.map(el => el.text);

                // все минус-фразы
                const commonMinusPhrases = phrasesSelectorWizard.minusWordsList
                    .map(el => el.text)
                    .concat(action.items.map(el => el.text));

                const request = keywordsStatRequest({
                    groupType,
                    phrasesList,
                    commonMinusPhrases,
                    regionsIds: phrasesSelectorWizard.groupInfo.regionIds,
                    apiManager: deps.restApiManager
                });

                return from(request).pipe(
                    mergeMap((result: GetKeywordStatShowResponse[]): Action[] => {
                        const err = result.find(res => res.advqErrors.length > 0);
                        if (err) {
                            return [
                                updatePhrasesSelectorWizardState(action.wizardId, {
                                    isInProgress: false
                                }),
                                updatePhrasesWizardError(
                                    action.wizardId,
                                    i18n('invalid-query'),
                                    PhrasesSelectorInputColumns.MINUS_WORDS
                                )
                            ];
                        }
                        const keywordsStat = phrasesList.reduce((acc, el, index) => {
                            acc[el] = {
                                searchedWith: result[index].searchedWith.map(mapPhraseToPhrasesSelectorListItem),
                                searchedAlso: result[index].searchedAlso.map(mapPhraseToPhrasesSelectorListItem)
                            };

                            return acc;
                        }, {});

                        const newPhrases = phrasesList.filter(
                            el => !phrasesSelectorWizard.startPhrases.find(item => item.text === el)
                        );

                        const newStartPhrasesList = newPhrases.map(el =>
                            mapPhraseToPhrasesSelectorListItem({
                                shows: 0,
                                phrase: el
                            })
                        );

                        const startPhrases = uniqBy(
                            [...phrasesSelectorWizard.startPhrases, ...newStartPhrasesList],
                            'text'
                        );

                        const actions = compact([
                            updatePhrasesSelectorWizardState(action.wizardId, {
                                keywordsStat,
                                startPhrases,
                                isInProgress: false
                            }),
                            selectionChange(
                                action.wizardId,
                                startPhrases.map(el => el.selected || newPhrases.includes(el.text)),
                                SelectionWizardListName.startPhrases
                            ),
                            addPhrasesToMinusWordsList(action.wizardId, action.items)
                        ]);

                        return actions;
                    })
                );
            }
        )
    );
};

export const removeMinusPhraseEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST),
        mergeMap(
            (action: RemovePhrasesToMinusWordsList): Observable<Action> => {
                const state = store$.value;

                const phrasesSelectorWizard = state.phrasesSelectorWizard[action.wizardId];
                const groupType = phrasesSelectorWizard.groupInfo.groupType;

                // старт-фразы
                const phrasesList = phrasesSelectorWizard.startPhrases.map(el => el.text);

                // все минус-фразы
                const commonMinusPhrases = phrasesSelectorWizard.minusWordsList.map(el => el.text);

                const request = keywordsStatRequest({
                    groupType,
                    phrasesList,
                    commonMinusPhrases,
                    regionsIds: phrasesSelectorWizard.groupInfo.regionIds,
                    apiManager: deps.restApiManager
                });

                return from(request).pipe(
                    mergeMap((result: GetKeywordStatShowResponse[]): Action[] => {
                        const err = result.find(res => res.advqErrors.length > 0);
                        if (err) {
                            return [
                                updatePhrasesSelectorWizardState(action.wizardId, {
                                    isInProgress: false
                                }),
                                updatePhrasesWizardError(
                                    action.wizardId,
                                    i18n('invalid-query'),
                                    PhrasesSelectorInputColumns.MINUS_WORDS
                                )
                            ];
                        }
                        const keywordsStat = phrasesList.reduce((acc, el, index) => {
                            acc[el] = {
                                searchedWith: result[index].searchedWith.map(mapPhraseToPhrasesSelectorListItem),
                                searchedAlso: result[index].searchedAlso.map(mapPhraseToPhrasesSelectorListItem)
                            };

                            return acc;
                        }, {});

                        const newPhrases = phrasesList.filter(
                            el => !phrasesSelectorWizard.startPhrases.find(item => item.text === el)
                        );

                        const newStartPhrasesList = newPhrases.map(el =>
                            mapPhraseToPhrasesSelectorListItem({
                                shows: 0,
                                phrase: el
                            })
                        );

                        const startPhrases = uniqBy(
                            [...phrasesSelectorWizard.startPhrases, ...newStartPhrasesList],
                            'text'
                        );

                        const actions = compact([
                            updatePhrasesSelectorWizardState(action.wizardId, {
                                keywordsStat,
                                startPhrases,
                                isInProgress: false
                            }),
                            selectionChange(
                                action.wizardId,
                                startPhrases.map(el => el.selected || newPhrases.includes(el.text)),
                                SelectionWizardListName.startPhrases
                            )
                        ]);

                        return actions;
                    })
                );
            }
        )
    );
};

export const submitSelectionWizardEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD).mergeMap(
        (action: SubmitSelectionWizard): Observable<Action> => {
            const { commonData } = store$.value;
            const phrasesSelectorWizard = store$.value.phrasesSelectorWizard[action.wizardId];
            const campaignType = phrasesSelectorWizard.groupInfo.campaign.type;
            const login = commonData.client.login;

            const adGroupId = phrasesSelectorWizard.groupInfo.id;
            const priceContext = phrasesSelectorWizard.priceContext;

            const addItems = phrasesSelectorWizard.chosenPhrases.map(el => ({
                keyword: el.text,
                adGroupId,
                priceContext: priceContext ? String(priceContext) : undefined
            }));

            const minusKeywords = phrasesSelectorWizard.minusWordsList.map(el => el.text);

            if (!login) {
                throw new Error('login is undefined');
            }

            const addKeywordsPromise = addItems.length
                ? deps.phrasesMutationExecutor
                      .addKeywords(
                          {
                              addItems
                          },
                          login
                      )
                      .then(response => response.addKeywords.addedItems.map(el => el.keywordId))
                : Promise.resolve([]);

            const addAddGroupMinusKeywordsPromise = minusKeywords.length
                ? deps.phrasesMutationExecutor
                      .addAdGroupMinusKeywords(
                          {
                              addItems: [
                                  {
                                      minusKeywords,
                                      adGroupId
                                  }
                              ]
                          },
                          login
                      )
                      .then(() => undefined)
                : Promise.resolve();

            return Observable.fromPromise(Promise.all([addKeywordsPromise, addAddGroupMinusKeywordsPromise]))
                .mergeMap(
                    ([addKeywordsResponse]): Observable<Action> => {
                        const filterParams = serializeFilterToString([
                            {
                                field: phraseIdFieldName,
                                operator: Operator.EQUALS,
                                value: {
                                    type: FilterExpressionValueType.numberSet,
                                    value: addKeywordsResponse
                                }
                            }
                        ]);

                        const closeSelectionWizard$ = Observable.of(closePhrasesSelectorWizard(action.wizardId)).merge(
                            Observable.of(
                                updatePhrasesSelectorWizardState(action.wizardId, {
                                    isInProgress: false,
                                    isVisible: false
                                })
                            )
                        );

                        if (addKeywordsResponse.length) {
                            return closeSelectionWizard$.merge(
                                Observable.of(
                                    changeLocationHard(
                                        UrlHelpers.createNewDirectLink(
                                            UrlHelpers.createNewDirectUrlPathByParams(GRID_PREFIX, phrases),
                                            {
                                                ulogin: login,
                                                filter: filterParams,
                                                [GridScreenUrlSearchQueryParams.campaignsIds]: String(
                                                    phrasesSelectorWizard.groupInfo.campaign.id
                                                ),
                                                [GridScreenUrlSearchQueryParams.groupsIds]: String(
                                                    phrasesSelectorWizard.groupInfo.id
                                                ),
                                                [GridScreenUrlSearchQueryParams.calcType]: mapCampaignTypeToCalcType(
                                                    campaignType
                                                )
                                            }
                                        )
                                    )
                                )
                            );
                        }

                        return closeSelectionWizard$;
                    }
                )
                .catch((err: ApiMethodExecutingError) => {
                    if (!(err instanceof NotSyncedError)) {
                        const error = err.getErrors()[0];
                        showNoticeMessage(NoticeType.error, mapMinusPhraseValidationResultApiData(error));
                    }

                    return Observable.of(
                        updatePhrasesSelectorWizardState(action.wizardId, {
                            isInProgress: false
                        })
                    );
                });
        }
    );
};

export const addPhraseToStartPhrasesEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$
        .ofType(PHRASES_SELECTOR_ADD_PHRASES_TO_START_LIST)
        .map((action: AddSearchedAlsoPhrasesToStartPhrases) => {
            const newPhrases = action.items.map(el => el.text);

            return addPhrasesStartInputItems(action.wizardId, newPhrases);
        });
};

export const openPhrasesSelectorWizardEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_PHRASES_SELECTOR_WIZARD),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: OpenPhrasesSelectorWizard): Observable<Action> => {
                const type = action.groupInfo.campaign.type;

                if (type === CampaignType.DYNAMIC) {
                    showNoticeMessage(NoticeType.error, i18n('no-phrases-for-dynamic-campaigns'));

                    return EMPTY;
                }

                if (type === CampaignType.PERFORMANCE) {
                    showNoticeMessage(NoticeType.error, i18n('no-phrases-for-smart-banners'));

                    return EMPTY;
                }

                return from(RegionsHelpers.fetchRegions()).pipe(
                    mergeMap(regionsResult => {
                        const showSelectionWizard: Array<Action> = [showPhrasesSelectorWizard(action.wizardId)];

                        if (!store$.value.commonData.regions) {
                            showSelectionWizard.push(updateRegions(regionsResult));
                        }

                        return showSelectionWizard;
                    })
                );
            }
        )
    );
};

export const fetchPhrasesForPhrasesSelectorWizardEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: FetchPhrasesForPhrasesSelectorWizardAction): Observable<Action> => {
                return from(
                    deps.groupsDataProvider.getGroupKeywordsList(
                        store$.value.commonData.login,
                        action.groupInfo.campaign.id,
                        action.groupInfo.id
                    )
                ).pipe(map(keywordsResult => addPhrasesStartInputItems(action.wizardId, keywordsResult)));
            }
        )
    );
};
